import { fetchIllustrationBootstrap, fetchIllustrationBundle, IllustrationBundleHttpError } from './api';
import type {
    IllustrationBundleBootstrap,
    IllustrationBundleCondition,
    IllustrationBundleConditionResult,
    IllustrationBundleImage,
    IllustrationBundleQuery,
    IllustrationFacetGroup,
} from './api';
import { FavoriteImage } from '../utils/visitorFavorites';

export const illustrationRoots = ['recent', 'popular', 'character', 'signature', 'scene', 'theme', 'rating', 'favorites'] as const;
export type IllustrationRoot = typeof illustrationRoots[number];
export const attributeRoots = ['character', 'signature', 'scene', 'theme'] as const;
export type AttributeRoot = typeof attributeRoots[number];

const BATCH_WINDOW_MS = 30;
const MAX_QUERIES_PER_BATCH = 8;
const MAX_ACTIVE_BATCHES = 2;
const MAX_PENDING_CONDITIONS = 256;
const MAX_CACHED_CONDITIONS = 100;
const CONDITION_CACHE_TTL_MS = 60000;
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 250;

type ConditionResult = { items: IllustrationBundleImage[]; total: number };
type ConditionState = 'queued' | 'in_flight' | 'retry_wait' | 'complete' | 'failed' | 'cancelled';
type ConditionEntry = {
    key: string;
    condition: IllustrationBundleCondition;
    promise: Promise<ConditionResult>;
    resolve: (value: ConditionResult) => void;
    reject: (error: unknown) => void;
    state: ConditionState;
    users: number;
    attempts: number;
    completedAt: number;
    value?: ConditionResult;
    batch?: BundleBatch;
    retryTimer?: ReturnType<typeof setTimeout>;
};
type BundleBatch = { controller: AbortController; entries: ConditionEntry[] };
type BootstrapRequest = { controller: AbortController; promise: Promise<IllustrationBundleBootstrap>; users: number };

const conditionEntries = new Map<string, ConditionEntry>();
const conditionQueue: ConditionEntry[] = [];
const activeBatches = new Set<BundleBatch>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let bootstrapRequest: BootstrapRequest | null = null;
let nextRequestId = 0;

function abortError(): DOMException {
    return new DOMException('Aborted', 'AbortError');
}

function isRecord(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: string[]): boolean {
    return Object.keys(value).sort().join('\0') === [...expected].sort().join('\0');
}

function isCount(value: unknown): value is number {
    return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isPreviewImage(value: unknown): value is IllustrationBundleImage {
    if (!isRecord(value) || !Number.isSafeInteger(value.id) || value.id <= 0 || typeof value.hash !== 'string'
        || !(typeof value.time === 'string' || (typeof value.time === 'number' && Number.isFinite(value.time)))
        || typeof value.thumb !== 'string'
        || !(value.published_at === null || typeof value.published_at === 'string'
            || (typeof value.published_at === 'number' && Number.isFinite(value.published_at)))
        || !Object.prototype.hasOwnProperty.call(value, 'index_created_at')
        || !(value.index_created_at === null || Number.isSafeInteger(value.index_created_at))) return false;
    if (value.public_title !== undefined && typeof value.public_title !== 'string') return false;
    return true;
}

function validateBootstrap(value: unknown): IllustrationBundleBootstrap {
    if (!isRecord(value) || !hasExactKeys(value, ['schema_version', 'as_of', 'groups', 'ratings', 'unrated_count', 'recent'])
        || value.schema_version !== 1 || !isCount(value.as_of) || !isRecord(value.groups)
        || !Array.isArray(value.ratings) || value.ratings.length !== 5 || !isCount(value.unrated_count)
        || !isRecord(value.recent) || !hasExactKeys(value.recent, ['items', 'total'])
        || !Array.isArray(value.recent.items) || value.recent.items.length > 10
        || !isCount(value.recent.total) || value.recent.total < value.recent.items.length
        || !value.recent.items.every(isPreviewImage)) throw new Error('Invalid illustration bootstrap');

    const ratingValues = new Set<number>();
    for (const rating of value.ratings) {
        if (!isRecord(rating) || !hasExactKeys(rating, ['value', 'count'])
            || !Number.isInteger(rating.value) || rating.value < 1 || rating.value > 5
            || !isCount(rating.count) || ratingValues.has(rating.value)) throw new Error('Invalid illustration bootstrap');
        ratingValues.add(rating.value);
    }
    for (const group of Object.values(value.groups)) {
        if (!isRecord(group) || !hasExactKeys(group, ['label', 'count', 'values'])
            || typeof group.label !== 'string' || !isCount(group.count) || !Array.isArray(group.values)
            || group.values.some(item => !isRecord(item) || !hasExactKeys(item, ['slug', 'label', 'count'])
                || typeof item.slug !== 'string' || typeof item.label !== 'string' || !isCount(item.count))) {
            throw new Error('Invalid illustration bootstrap');
        }
    }
    return value as IllustrationBundleBootstrap;
}

function validateConditionResult(value: unknown, requestId: string): IllustrationBundleConditionResult {
    if (!isRecord(value) || value.request_id !== requestId) throw new Error('Invalid illustration query response');
    if (Object.prototype.hasOwnProperty.call(value, 'error')) {
        if (!hasExactKeys(value, ['request_id', 'error']) || !isRecord(value.error) || !hasExactKeys(value.error, ['code'])
            || typeof value.error.code !== 'string') throw new Error('Invalid illustration query response');
        return value as IllustrationBundleConditionResult;
    }
    if (!hasExactKeys(value, ['request_id', 'items', 'total']) || !Array.isArray(value.items)
        || value.items.length > 10 || !isCount(value.total) || value.total < value.items.length
        || !value.items.every(isPreviewImage)
        || new Set(value.items.map((item: IllustrationBundleImage) => item.id)).size !== value.items.length) {
        throw new Error('Invalid illustration query response');
    }
    return value as IllustrationBundleConditionResult;
}

function validateBundleResponse(value: unknown, expectedIds: Set<string>): Map<string, IllustrationBundleConditionResult> {
    if (!isRecord(value) || !hasExactKeys(value, ['schema_version', 'as_of', 'results'])
        || value.schema_version !== 1 || !isCount(value.as_of) || !Array.isArray(value.results)
        || value.results.length !== expectedIds.size) throw new Error('Invalid illustration bundle response');
    const results = new Map<string, IllustrationBundleConditionResult>();
    for (const raw of value.results) {
        if (!isRecord(raw) || typeof raw.request_id !== 'string' || !expectedIds.has(raw.request_id) || results.has(raw.request_id)) {
            throw new Error('Invalid illustration bundle response');
        }
        results.set(raw.request_id, validateConditionResult(raw, raw.request_id));
    }
    if (results.size !== expectedIds.size) throw new Error('Invalid illustration bundle response');
    return results;
}

function removeEntry(entry: ConditionEntry, error: unknown): void {
    if (entry.retryTimer) clearTimeout(entry.retryTimer);
    entry.retryTimer = undefined;
    entry.state = 'cancelled';
    entry.batch = undefined;
    if (conditionEntries.get(entry.key) === entry) conditionEntries.delete(entry.key);
    entry.reject(error);
}

function trimConditionCache(): void {
    const now = Date.now();
    let cached = Array.from(conditionEntries.values()).filter(entry => entry.state === 'complete');
    for (const entry of cached) {
        if (now - entry.completedAt > CONDITION_CACHE_TTL_MS && conditionEntries.get(entry.key) === entry) {
            conditionEntries.delete(entry.key);
        }
    }
    cached = Array.from(conditionEntries.values()).filter(entry => entry.state === 'complete')
        .sort((a, b) => a.completedAt - b.completedAt);
    while (cached.length > MAX_CACHED_CONDITIONS) {
        const entry = cached.shift()!;
        if (conditionEntries.get(entry.key) === entry) conditionEntries.delete(entry.key);
    }
}

function pendingConditionCount(): number {
    let count = 0;
    for (const entry of Array.from(conditionEntries.values())) {
        if (entry.state === 'queued' || entry.state === 'retry_wait') count++;
    }
    return count;
}

function scheduleFlush(): void {
    if (flushTimer || !conditionQueue.some(entry => entry.state === 'queued')) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flushQueue();
    }, BATCH_WINDOW_MS);
}

function shouldRetry(error: unknown): boolean {
    if (error instanceof IllustrationBundleHttpError) return error.status === 429 || error.status >= 500;
    return true;
}

function failCondition(entry: ConditionEntry, error: unknown, retryable: boolean): void {
    entry.batch = undefined;
    if (entry.users === 0) {
        removeEntry(entry, abortError());
        return;
    }
    if (retryable && entry.attempts < MAX_ATTEMPTS) {
        entry.state = 'retry_wait';
        entry.retryTimer = setTimeout(() => {
            entry.retryTimer = undefined;
            if (entry.state !== 'retry_wait') return;
            if (entry.users === 0) {
                removeEntry(entry, abortError());
                return;
            }
            entry.state = 'queued';
            conditionQueue.push(entry);
            scheduleFlush();
        }, RETRY_DELAY_MS);
        return;
    }
    entry.state = 'failed';
    if (conditionEntries.get(entry.key) === entry) conditionEntries.delete(entry.key);
    entry.reject(error);
}

function completeCondition(entry: ConditionEntry, value: ConditionResult): void {
    if (entry.users === 0) {
        removeEntry(entry, abortError());
        return;
    }
    entry.state = 'complete';
    entry.batch = undefined;
    entry.completedAt = Date.now();
    entry.value = value;
    entry.resolve(value);
    trimConditionCache();
}

function startBatch(entries: ConditionEntry[]): void {
    const controller = new AbortController();
    const batch: BundleBatch = { controller, entries };
    const queryIds = new Set<string>();
    const queries: IllustrationBundleQuery[] = entries.map(entry => {
        nextRequestId++;
        const requestId = `illustration-${nextRequestId.toString(36)}`;
        queryIds.add(requestId);
        return { ...entry.condition, request_id: requestId } as IllustrationBundleQuery;
    });
    entries.forEach(entry => { entry.state = 'in_flight'; entry.batch = batch; entry.attempts++; });
    activeBatches.add(batch);

    void fetchIllustrationBundle(queries, controller.signal)
        .then(response => validateBundleResponse(response, queryIds))
        .then(results => {
            entries.forEach((entry, index) => {
                const result = results.get(queries[index].request_id)!;
                if ('error' in result) {
                    const error = new Error(`Illustration condition failed: ${result.error.code}`);
                    failCondition(entry, error, result.error.code !== 'invalid_query');
                } else {
                    completeCondition(entry, { items: result.items, total: result.total });
                }
            });
        })
        .catch(error => {
            entries.forEach(entry => failCondition(entry, error, shouldRetry(error)));
        })
        .finally(() => {
            activeBatches.delete(batch);
            scheduleFlush();
        });
}

function flushQueue(): void {
    while (activeBatches.size < MAX_ACTIVE_BATCHES) {
        const entries: ConditionEntry[] = [];
        while (conditionQueue.length && entries.length < MAX_QUERIES_PER_BATCH) {
            const entry = conditionQueue.shift()!;
            if (entry.state !== 'queued') continue;
            if (entry.users === 0) {
                removeEntry(entry, abortError());
                continue;
            }
            entries.push(entry);
        }
        if (!entries.length) return;
        startBatch(entries);
    }
}

function subscribeCondition(entry: ConditionEntry, signal: AbortSignal): Promise<ConditionResult> {
    if (signal.aborted) return Promise.reject(abortError());
    entry.users++;
    return new Promise((resolve, reject) => {
        let finished = false;
        const finish = (action: () => void) => {
            if (finished) return;
            finished = true;
            signal.removeEventListener('abort', abort);
            entry.users = Math.max(0, entry.users - 1);
            if (entry.users === 0 && entry.state !== 'complete' && entry.state !== 'failed' && entry.state !== 'cancelled') {
                if (entry.state === 'in_flight') {
                    if (entry.batch?.entries.every(item => item.users === 0)) entry.batch.controller.abort();
                } else {
                    removeEntry(entry, abortError());
                }
            }
            action();
        };
        const abort = () => finish(() => reject(abortError()));
        signal.addEventListener('abort', abort, { once: true });
        entry.promise.then(value => finish(() => resolve(value)), error => finish(() => reject(error)));
        if (signal.aborted) abort();
    });
}

function enqueueCondition(condition: IllustrationBundleCondition, signal: AbortSignal): Promise<ConditionResult> {
    if (signal.aborted) return Promise.reject(abortError());
    trimConditionCache();
    const key = JSON.stringify(condition);
    let entry = conditionEntries.get(key);
    if (entry?.state === 'complete') {
        if (condition.kind !== 'recent' && Date.now() - entry.completedAt <= CONDITION_CACHE_TTL_MS && entry.value) {
            conditionEntries.delete(key);
            conditionEntries.set(key, entry);
            return Promise.resolve(entry.value);
        }
        conditionEntries.delete(key);
        entry = undefined;
    }
    if (!entry) {
        if (pendingConditionCount() >= MAX_PENDING_CONDITIONS) {
            return Promise.reject(new Error('検索要求が混み合っています。時間をおいて再試行してください。'));
        }
        let resolve!: (value: ConditionResult) => void;
        let reject!: (error: unknown) => void;
        const promise = new Promise<ConditionResult>((resolvePromise, rejectPromise) => {
            resolve = resolvePromise;
            reject = rejectPromise;
        });
        void promise.catch(() => {});
        entry = { key, condition, promise, resolve, reject, state: 'queued', users: 0, attempts: 0, completedAt: 0 };
        conditionEntries.set(key, entry);
        conditionQueue.push(entry);
        scheduleFlush();
    }
    return subscribeCondition(entry, signal);
}

function subscribeBootstrap(request: BootstrapRequest, signal: AbortSignal): Promise<IllustrationBundleBootstrap> {
    if (signal.aborted) return Promise.reject(abortError());
    request.users++;
    return new Promise((resolve, reject) => {
        let finished = false;
        const finish = (action: () => void) => {
            if (finished) return;
            finished = true;
            signal.removeEventListener('abort', abort);
            request.users = Math.max(0, request.users - 1);
            if (request.users === 0 && bootstrapRequest === request && !request.controller.signal.aborted) request.controller.abort();
            action();
        };
        const abort = () => finish(() => reject(abortError()));
        signal.addEventListener('abort', abort, { once: true });
        request.promise.then(value => finish(() => resolve(value)), error => finish(() => reject(error)));
        if (signal.aborted) abort();
    });
}

export function loadIllustrationIndex(signal: AbortSignal): Promise<IllustrationBundleBootstrap> {
    if (signal.aborted) return Promise.reject(abortError());
    if (!bootstrapRequest || bootstrapRequest.controller.signal.aborted) {
        const controller = new AbortController();
        const request: BootstrapRequest = { controller, users: 0, promise: Promise.resolve(null as unknown as IllustrationBundleBootstrap) };
        request.promise = fetchIllustrationBootstrap(controller.signal)
            .then(validateBootstrap)
            .then(value => {
                if (bootstrapRequest === request) bootstrapRequest = null;
                return value;
            }, error => {
                if (bootstrapRequest === request) bootstrapRequest = null;
                throw error;
            });
        bootstrapRequest = request;
    }
    return subscribeBootstrap(bootstrapRequest, signal);
}

function conditionFor(root: IllustrationRoot, value: string): IllustrationBundleCondition {
    if (root === 'recent') return { kind: 'recent' };
    if (root === 'rating') {
        const rating = Number(value);
        if (!Number.isInteger(rating) || rating < 1 || rating > 5) throw new Error('Invalid illustration rating');
        return { kind: 'rating', value: rating };
    }
    if (root === 'favorites') throw new Error('Saved conditions are built from image references');
    return { kind: 'facet', group: root as IllustrationFacetGroup, value };
}

export async function loadIllustrationCondition(
    root: IllustrationRoot,
    value: string,
    signal: AbortSignal,
    favorites: FavoriteImage[] = []
): Promise<ConditionResult> {
    if (root !== 'favorites') return enqueueCondition(conditionFor(root, value), signal);
    const uniqueFavorites = Array.from(new Map(favorites.map(item => [item.id, item])).values());
    if (!uniqueFavorites.length) return { items: [], total: 0 };
    const chunks: ConditionResult[] = await Promise.all(Array.from({ length: Math.ceil(uniqueFavorites.length / 40) }, (_, index) => {
        const images = uniqueFavorites.slice(index * 40, (index + 1) * 40)
            .map(({ id, hash }) => ({ id, hash: hash.toLowerCase() }))
            .sort((a, b) => a.id - b.id || a.hash.localeCompare(b.hash));
        return enqueueCondition({ kind: 'saved', images }, signal);
    }));
    const total = chunks.reduce((sum, chunk) => sum + chunk.total, 0);
    if (!Number.isSafeInteger(total)) throw new Error('Invalid illustration count');
    const items = Array.from(new Map(chunks.flatMap(chunk => chunk.items).map(item => [`${item.id}:${item.hash.toLowerCase()}`, item])).values())
        .sort((a, b) => (b.index_created_at ?? -Infinity) - (a.index_created_at ?? -Infinity) || b.id - a.id)
        .slice(0, 10);
    return { items, total };
}

export async function loadIllustrationPreview(root: IllustrationRoot, value: string, signal: AbortSignal, favorites: FavoriteImage[] = []): Promise<IllustrationBundleImage[]> {
    return (await loadIllustrationCondition(root, value, signal, favorites)).items;
}
